import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import api from '@/lib/api-client';

export function useConfigurationUploadStatus(
  configurationId: string,
  enabled = true,
) {
  return useQuery({
    queryKey: ['configuration-upload-status', configurationId],
    queryFn: async () => {
      const response = await api.get(
        `/v1/admin/configurations/${configurationId}/upload-status`,
      );
      return response;
    },
    enabled,
    refetchInterval: (data) => {
      const shouldPoll = data?.isProcessing === true;
      return shouldPoll ? 2000 : false;
    },
    refetchIntervalInBackground: false,
    staleTime: 1000,
  });
}

export function useConfigurationsUploadStatus(configurationIds: string[]) {
  const queryClient = useQueryClient();
  const previousStatusesRef = useRef<Record<string, string>>({});

  const query = useQuery({
    queryKey: ['configurations-upload-status', configurationIds.sort()],
    queryFn: async () => {
      const promises = configurationIds.map((id) =>
        api
          .get(`/v1/admin/configurations/${id}/upload-status`)
          .catch((error) => {
            console.log(`Error fetching status for ${id}:`, error);
            return null;
          }),
      );
      const results = await Promise.all(promises);

      const statusMap: Record<string, any> = {};
      results.forEach((result: any, index: any) => {
        if (result) {
          statusMap[configurationIds[index]] = result;
        }
      });

      return statusMap;
    },
    enabled: configurationIds.length > 0,
    refetchInterval: (data) => {
      if (!data) {
        return false;
      }

      const hasProcessing = Object.values(data).some((status: any) => {
        const isProcessing = status?.isProcessing === true;
        console.log(`Config ${status?.configurationId} processing:`, isProcessing);
        return isProcessing;
      });
      
      return hasProcessing ? 2000 : false;
    },
    refetchIntervalInBackground: false,
    staleTime: 1000,
  });

  useEffect(() => {
    if (!query.data) return;

    let shouldInvalidateConfigurations = false;

    Object.entries(query.data).forEach(([configId, statusData]: [string, any]) => {
      const currentStatus = statusData?.latestStatus?.status;
      const previousStatus = previousStatusesRef.current[configId];
      const wasProcessing = previousStatus === 'processing';
      const isNowComplete = currentStatus === 'completed' || currentStatus === 'failed';

      console.log(`Config ${configId}:`, {
        previousStatus,
        currentStatus,
        wasProcessing,
        isNowComplete
      });

      if (wasProcessing && isNowComplete) {
        console.log(`Upload ${currentStatus} for configuration ${configId}`);
        shouldInvalidateConfigurations = true;
      }

      previousStatusesRef.current[configId] = currentStatus;
    });

    if (shouldInvalidateConfigurations) {
      queryClient.invalidateQueries({ 
        queryKey: ['configurations'],
        exact: false 
      });
    }
  }, [query.data, queryClient]);

  return query;
}